/**
 * Cookie consent banner for React + GTM (Google Consent Mode v2).
 * Library: vanilla-cookieconsent (MIT).
 *
 * Flow in this project:
 * 1. src/config/analytics.ts sets `consent: default` = denied and injects GTM
 *    (production + prod-host only). It does NOT inject Yandex.Metrika.
 * 2. On the user's choice this banner pushes `consent: update` for Google tags
 *    (GTM decides what fires) AND, on analytics acceptance, calls
 *    grantAnalyticsConsent() to load Yandex.Metrika — which is injected directly
 *    in code and cannot be gated by GTM/Consent Mode.
 * 3. Mounted once at the app root: <CookieConsentBanner />.
 *
 * gtag/dataLayer only exist in production on a prod host (see analytics.ts), so
 * every window.gtag / window.dataLayer access here is guarded — on dev/staging
 * the banner shows but the consent pushes are safe no-ops.
 */

import { useEffect } from "react";
import * as CookieConsent from "vanilla-cookieconsent";
import "vanilla-cookieconsent/dist/cookieconsent.css";
import {
  grantAnalyticsConsent,
  isAnalyticsEnabled,
} from "../../config/analytics";

function updateConsent(): void {
  // The banner itself is pure cookie UI and shows everywhere. Only the actual
  // wiring to trackers is host-gated: no consent signals are sent, and no
  // Yandex.Metrika is loaded, outside client.promobile.app.
  if (!isAnalyticsEnabled()) {
    return;
  }

  const analytics = CookieConsent.acceptedCategory("analytics");
  const marketing = CookieConsent.acceptedCategory("marketing");

  // Google tags via GTM (GA4, plus any ad tags configured in the container).
  window.gtag?.("consent", "update", {
    analytics_storage: analytics ? "granted" : "denied",
    ad_storage: marketing ? "granted" : "denied",
    ad_user_data: marketing ? "granted" : "denied",
    ad_personalization: marketing ? "granted" : "denied",
  });

  // Custom event for any GTM triggers that key off consent changes.
  window.dataLayer?.push({ event: "cc_consent_update" });

  // Yandex.Metrika is injected directly in code (not through GTM), so Consent
  // Mode cannot gate it — load it here, only after analytics consent.
  if (analytics) {
    grantAnalyticsConsent();
  }
}

export default function CookieConsentBanner(): null {
  useEffect(() => {
    CookieConsent.run({
      guiOptions: {
        consentModal: {
          layout: "bar inline",
          position: "bottom",
          equalWeightButtons: true,
        },
        preferencesModal: { layout: "box" },
      },

      categories: {
        necessary: { enabled: true, readOnly: true },
        analytics: {},
        marketing: {},
      },

      // Fires on the first choice and on every load with a stored consent.
      onConsent: updateConsent,
      // Fires when the user changes their choice via "Cookie settings".
      onChange: updateConsent,

      language: {
        default: "en",
        autoDetect: "browser",
        translations: {
          en: {
            consentModal: {
              title: "We use cookies",
              description:
                "We use cookies to analyze traffic and improve your experience. You can accept all cookies or manage your preferences.",
              acceptAllBtn: "Accept all",
              acceptNecessaryBtn: "Reject all",
              showPreferencesBtn: "Manage preferences",
            },
            preferencesModal: {
              title: "Cookie preferences",
              acceptAllBtn: "Accept all",
              acceptNecessaryBtn: "Reject all",
              savePreferencesBtn: "Save preferences",
              sections: [
                {
                  title: "Strictly necessary",
                  description:
                    "Required for the website to function. Always on.",
                  linkedCategory: "necessary",
                },
                {
                  title: "Analytics",
                  description:
                    "Help us understand how visitors use the site so we can improve it.",
                  linkedCategory: "analytics",
                },
                {
                  title: "Marketing",
                  description:
                    "Used to measure ad performance. See our privacy policy for details.",
                  linkedCategory: "marketing",
                },
              ],
            },
          },
          ru: {
            consentModal: {
              title: "Мы используем cookies",
              description:
                "Cookies помогают нам анализировать трафик и улучшать сайт. Можно принять все или настроить по категориям.",
              acceptAllBtn: "Принять все",
              acceptNecessaryBtn: "Отклонить",
              showPreferencesBtn: "Настроить",
            },
            preferencesModal: {
              title: "Настройки cookies",
              acceptAllBtn: "Принять все",
              acceptNecessaryBtn: "Отклонить",
              savePreferencesBtn: "Сохранить",
              sections: [
                {
                  title: "Необходимые",
                  description: "Нужны для работы сайта. Всегда включены.",
                  linkedCategory: "necessary",
                },
                {
                  title: "Аналитика",
                  description:
                    "Помогают понять, как посетители пользуются сайтом, чтобы улучшать его.",
                  linkedCategory: "analytics",
                },
                {
                  title: "Маркетинг",
                  description:
                    "Измерение эффективности рекламы. Подробнее — в privacy policy.",
                  linkedCategory: "marketing",
                },
              ],
            },
          },
        },
      },
    });
  }, []);

  return null;
}
